import React from 'react';
import EcoSpace from 'eco-design/base/eco-space';
import EcoSectionCommon from 'eco-design/elements/eco-section-common';
import EfImg from '@elem/ui/ef-img';
import { Tabs } from 'antd';
import { useMediaDevice } from '@elem/compPage/Oasis/hooks';
import styles from './index.module.scss';
import { Icon1, Icon2, Icon3, Icon4 } from './icons/index';
import { useLang } from '@/app/utils/useLang';
import gtm from '@/utils/gtm';

export default function Scene() {
    const { t } = useLang('referral.scene');

    const p1 = React.useMemo(
        () => (
            <>
                {t('t1_1')}{' '}
                {t.rich('d1_1', {
                    sup: (text: string) => <sup>{text}</sup>,
                })}
            </>
        ),
        [t],
    );

    const p2 = React.useMemo(
        () => (
            <>
                {t('t2_1')}{' '}
                {t.rich('d2_1', {
                    sup: (text: string) => <sup>{text}</sup>,
                })}
            </>
        ),
        [t],
    );

    const p3 = React.useMemo(
        () => (
            <>
                {t('t3_1')}{' '}
                {t.rich('d3_1', {
                    sup: (text: string) => <sup>{text}</sup>,
                })}
            </>
        ),
        [t],
    );

    const [desc, setDesc] = React.useState(t('desc1') || '');
    const [paragraph, setParagraph] = React.useState(p1);
    const { isH5 } = useMediaDevice();

    const imageUrls = React.useMemo(
        () => ({
            tab1: {
                pc: 'https://websiteoss.ecoflow.com/upload/image/1748512760856/01_PC.jpg',
                h5: 'https://websiteoss.ecoflow.com/upload/image/1748512764782/01_M.jpg',
            },
            tab2: {
                pc: 'https://websiteoss.ecoflow.com/upload/image/1748512768400/02_PC.jpg',
                h5: 'https://websiteoss.ecoflow.com/upload/image/1748512771933/02_M.jpg',
            },
            tab3: {
                pc: 'https://websiteoss.ecoflow.com/upload/image/1748512775125/03_PC.jpg',
                h5: 'https://websiteoss.ecoflow.com/upload/image/1748512778045/03_M.jpg',
            },
        }),
        [],
    );

    React.useEffect(() => {
        const preloadImages = () => {
            Object.values(imageUrls).forEach(({ pc, h5 }) => {
                if (!isH5) {
                    const pcImg = new Image();
                    pcImg.src = pc;
                } else {
                    const h5Img = new Image();
                    h5Img.src = h5;
                }
            });
        };

        preloadImages();
    }, [imageUrls, isH5]);

    return (
        <div className={styles.Scene}>
            <EcoSpace />
            <EcoSectionCommon title={t('title')} desc={desc} />
            <div className="terminal-text">{paragraph}</div>
            <div className="scene-content">
                <Tabs
                    centered
                    onChange={(tab) => {
                        if (tab === 't1') {
                            setDesc(t('desc1'));
                            setParagraph(p1);
                            try {
                                gtm.push({
                                    event: 'uaEvent',
                                    event_name: 'tab_click',
                                    list_name: `A Path to Zero: Empower Your Home with Solar Energy`,
                                    button_name: t('tab1'),
                                });
                            } catch (e) {
                                console.log(e);
                            }
                        } else if (tab === 't2') {
                            setDesc(t('desc2'));
                            setParagraph(p2);
                            try {
                                gtm.push({
                                    event: 'uaEvent',
                                    event_name: 'tab_click',
                                    list_name: `A Path to Zero: Empower Your Home with Solar Energy`,
                                    button_name: t('tab2'),
                                });
                            } catch (e) {
                                console.log(e);
                            }
                        } else if (tab === 't3') {
                            setDesc(t('desc3'));
                            setParagraph(p3);
                            try {
                                gtm.push({
                                    event: 'uaEvent',
                                    event_name: 'tab_click',
                                    list_name: `A Path to Zero: Empower Your Home with Solar Energy`,
                                    button_name: t('tab3'),
                                });
                            } catch (e) {
                                console.log(e);
                            }
                        }
                    }}
                >
                    <Tabs.TabPane tab={t('tab1')} key="t1">
                        <div className="scene-item">
                            <EfImg className="item-img" pc={imageUrls.tab1.pc} h5={imageUrls.tab1.h5} />
                            <div className="item-info">
                                <div className="item-info-title">{t('t1_1')}</div>
                                <div className="item-info-desc">
                                    {t.rich('d1_1', {
                                        sup: (text: string) => <sup>{text}</sup>,
                                    })}
                                </div>
                                <div className="item-info-card">
                                    <div className="item-card-name">{t('t1_2')}</div>
                                    <div className="item-card-subtitle">{t('d1_2')}</div>
                                    <div className="item-card-divider"></div>
                                    <div className="item-card-icons">
                                        <div className="item-icon">
                                            <Icon1 />
                                        </div>
                                        <div className="item-icon">
                                            <Icon2 />
                                        </div>
                                        <div className="item-icon">
                                            <Icon3 />
                                        </div>
                                        <div className="item-icon">
                                            <Icon4 />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Tabs.TabPane>
                    <Tabs.TabPane tab={t('tab2')} key="t2">
                        <div className="scene-item">
                            <EfImg className="item-img" pc={imageUrls.tab2.pc} h5={imageUrls.tab2.h5} />
                            <div className="item-info">
                                <div className="item-info-title">{t('t2_1')}</div>
                                <div className="item-info-desc">
                                    {t.rich('d2_1', {
                                        sup: (text: string) => <sup>{text}</sup>,
                                    })}
                                </div>
                                <div className="item-info-card">
                                    <div className="item-card-name">{t('t2_2')}</div>
                                    <div className="item-card-subtitle">{t('d2_2')}</div>
                                    <div className="item-card-divider"></div>
                                    <div className="item-card-icons">
                                        <div className="item-icon">
                                            <Icon1 />
                                        </div>
                                        <div className="item-icon">
                                            <Icon2 />
                                        </div>
                                        <div className="item-icon">
                                            <Icon3 />
                                        </div>
                                        <div className="item-icon">
                                            <Icon4 />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Tabs.TabPane>
                    <Tabs.TabPane tab={t('tab3')} key="t3">
                        <div className="scene-item">
                            <EfImg className="item-img" pc={imageUrls.tab3.pc} h5={imageUrls.tab3.h5} />
                            <div className="item-info">
                                <div className="item-info-title">{t('t3_1')}</div>
                                <div className="item-info-desc">
                                    {t.rich('d3_1', {
                                        sup: (text: string) => <sup>{text}</sup>,
                                    })}
                                </div>
                                <div className="item-info-card">
                                    <div className="item-card-name">{t('t3_2')}</div>
                                    <div className="item-card-subtitle">{t('d3_2')}</div>
                                    <div className="item-card-divider"></div>
                                    <div className="item-card-icons">
                                        <div className="item-icon">
                                            <Icon1 />
                                        </div>
                                        <div className="item-icon">
                                            <Icon2 />
                                        </div>
                                        <div className="item-icon">
                                            <Icon3 />
                                        </div>
                                        <div className="item-icon">
                                            <Icon4 />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Tabs.TabPane>
                </Tabs>
            </div>
        </div>
    );
}
